import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { uploadPhotos, getPhotoStatus, ApiError } from "../../lib/api";

const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/heic,image/webp";
const POLL_MS = 5000;
const POLL_LIMIT_MS = 120_000; // BR-T-006: stop polling after 2 minutes

interface PhotoRow {
  photoId: number;
  fileName: string;
  scanStatus: string; // Pending | Clean | Malicious | Error | Rejected
}

function statusLabel(s: string): { text: string; cls: string } {
  switch (s) {
    case "Clean": return { text: "Accepted", cls: "text-green-700" };
    case "Pending": return { text: "Scanning…", cls: "text-gray-500" };
    default: return { text: "Not accepted", cls: "text-red-600" }; // Malicious / Error / Rejected — don't alarm
  }
}

function readToken(hash: string): string {
  // Token travels in the URL fragment (#token=...) so it isn't sent in Referer / server logs.
  const m = /[#&]token=([^&]+)/.exec(hash);
  return m && m[1] ? decodeURIComponent(m[1]) : "";
}

export default function EnquireConfirmationPage() {
  const location = useLocation();
  const token = readToken(location.hash);

  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollStart = useRef<number | null>(null);

  const onSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || !token) return;
    setError(null);
    const picked = Array.from(files).slice(0, MAX_FILES);
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is larger than 10 MB. Please choose a smaller image.`);
      return;
    }
    setUploading(true);
    try {
      const res = await uploadPhotos(token, picked);
      const rows: PhotoRow[] = (res.photos ?? []).map((p) => ({
        photoId: p.photoId,
        fileName: p.fileName || "photo",
        scanStatus: p.status || "Pending",
      }));
      setPhotos((prev) => [...prev, ...rows]);
      pollStart.current = Date.now();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Your upload session has expired. Please submit a new enquiry to add photos.");
      } else {
        setError("Some photos couldn't be uploaded. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  }, [token]);

  // Poll Pending photos every 5s, up to 2 minutes.
  useEffect(() => {
    const hasPending = photos.some((p) => p.scanStatus === "Pending");
    if (!token || !hasPending) return;
    if (pollStart.current === null) pollStart.current = Date.now();

    const interval = window.setInterval(async () => {
      if (pollStart.current !== null && Date.now() - pollStart.current > POLL_LIMIT_MS) {
        window.clearInterval(interval);
        return;
      }
      const pending = photos.filter((p) => p.scanStatus === "Pending");
      const updates = await Promise.all(
        pending.map(async (p) => {
          try {
            const s = await getPhotoStatus(token, p.photoId);
            return { photoId: p.photoId, scanStatus: s.scanStatus || "Pending" };
          } catch {
            return null;
          }
        }),
      );
      setPhotos((prev) =>
        prev.map((row) => {
          const u = updates.find((x) => x && x.photoId === row.photoId);
          return u ? { ...row, scanStatus: u.scanStatus } : row;
        }),
      );
    }, POLL_MS);

    return () => window.clearInterval(interval);
  }, [token, photos]);

  return (
    <div className="mx-auto max-w-xl">
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Thanks — we&apos;ve received your enquiry</h1>
        <p className="mt-2 text-sm text-gray-600">
          A local rep will be in touch within 2 business days. There&apos;s nothing else you need to do.
        </p>
      </div>

      {token ? (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">Add photos (optional)</h2>
          <p className="mt-1 text-sm text-gray-600">
            Photos of your space help us prepare a more accurate budget. Up to {MAX_FILES} images, 10 MB each.
          </p>

          <label className="mt-4 flex h-12 w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-gray-300 px-4 text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 sm:w-auto">
            {uploading ? "Uploading…" : "Choose photos"}
            <input type="file" accept={ACCEPT} multiple className="sr-only" disabled={uploading}
              onChange={(e) => onSelect(e.target.files)} />
          </label>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          {photos.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100 rounded-md border border-gray-100">
              {photos.map((p) => {
                const s = statusLabel(p.scanStatus);
                return (
                  <li key={p.photoId} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="truncate text-gray-700">{p.fileName}</span>
                    <span className={`ml-3 shrink-0 font-medium ${s.cls}`}>{s.text}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <p className="mt-6 text-center text-sm text-gray-500">
          If you&apos;d like to add photos to your enquiry, please submit the form again — your upload
          session link wasn&apos;t found.
        </p>
      )}
    </div>
  );
}
